import { ReactNode, useCallback } from 'react';
import React from 'react';
import {
  AppBridgeContext,
  RedirectAdminAction,
  RedirectAdminResource,
  RedirectPassThroughAction,
  ToastOptions,
} from 'containers/AppBridge';
import { useNavigate } from 'react-router';
import { useAppBridge } from '@shopify/app-bridge-react';

const ShopifyDriver: React.FC<{
  apiKey: string;
  host: string;
  children: ReactNode;
}> = ({ apiKey, host, children }) => {
  const shopify = useAppBridge();

  const navigate = useNavigate();

  const redirect = (
    action: RedirectPassThroughAction | RedirectAdminAction,
    resource: string | RedirectAdminResource
  ) => {
    if (action === 'App') {
      if (typeof resource !== 'string') {
        throw new Error(
          'Resource must be a string for an App or Remote redirect'
        );
      }
      navigate(resource);
    } else if (action === 'Remote') {
      if (typeof resource !== 'string') {
        throw new Error(
          'Resource must be a string for an App or Remote redirect'
        );
      }
      window.open(resource, '_top');
    } else {
      if (typeof resource === 'string') {
        throw new Error(`Resource must be of type RedirectAdminResource`);
      }
      switch (resource.type) {
        case 'checkout':
          window.open('shopify://admin/settings/checkout', '_top');
          break;
        case 'nft_application':
          window.open(
            `shopify://admin/nft-application?apiKey=${encodeURIComponent(
              resource.apiKey
            )}&callback=${encodeURIComponent(resource.callbackUrl)}`,
            '_top'
          );
          break;
        case 'product':
          window.open(
            `shopify://admin/products/${resource.id}`,
            resource.newContext ? '_blank' : '_top'
          );
          break;
        case 'subscription':
          window.open(
            `shopify://admin/settings/apps/app_installations/app/${process.env.REACT_APP_SHOPIFY_APP_HANDLE}`,
            '_top'
          );
          break;
        case 'shipping':
          window.open('shopify://admin/settings/shipping', '_top');
          break;
        default:
          throw new Error('Not implemented yet');
      }
    }
  };

  const showToast = (options: ToastOptions) => {
    // Function to attempt showing the toast
    const attemptShowToast = () => {
      if (shopify.toast) {
        try {
          const duration = options.duration || 3000;
          shopify.toast.show(options.message, {
            duration: duration,
          });
        } catch (error) {
          console.error('ShopifyDriver: Error showing toast:', error);
        }
      } else {
        console.warn(
          'ShopifyDriver: App Bridge Toast not available - shopify.toast is undefined'
        );
        // Retry after a short delay if App Bridge isn't loaded yet
        setTimeout(attemptShowToast, 100);
      }
    };

    // Initial attempt
    attemptShowToast();
  };

  const getSessionToken = useCallback(async () => {
    return shopify.idToken();
  }, [shopify]);

  const openInNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <AppBridgeContext.Provider
      value={{
        host: host,
        apiKey: apiKey,
        redirect,
        showToast,
        getSessionToken,
        platform: 'Shopify',
        openInNewTab,
      }}
    >
      {children}
    </AppBridgeContext.Provider>
  );
};

export default ShopifyDriver;
